import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, ExternalLink, IndianRupee, Heart, Gift, Loader2, Clock, CheckCircle, Copy, CreditCard, Check } from 'lucide-react';
import BottomNavigation from '../components/BottomNavigation';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const SharedWishlistViewPage = () => {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState(null);
  const [items, setItems] = useState([]);
  const [owner, setOwner] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [contributionData, setContributionData] = useState({
    amount: '',
    transaction_id: '',
    transaction_date: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchWishlist();
  }, [shareCode]);

  const fetchWishlist = async () => {
    try {
      // First find wishlist by share code
      const response = await axios.get(`${API}/wishlists/shared-with-me`, { withCredentials: true });
      const shared = response.data.wishlists || [];
      const found = shared.find(w => w.share_code === shareCode);
      
      if (found) {
        // Has access, fetch full details
        const detailResponse = await axios.get(`${API}/wishlists/${found.id}`, { withCredentials: true });
        setWishlist(detailResponse.data.wishlist);
        setItems(detailResponse.data.items || []);
        setOwner(detailResponse.data.owner);
        setIsOwner(detailResponse.data.is_owner);
      } else {
        // Check if user already has a pending request or needs to request
        setAccessDenied(true);
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load wishlist');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContribute = (item) => {
    setSelectedItem(item);
    setContributionData({ amount: '', transaction_id: '', transaction_date: '' });
    setShowContributeModal(true);
  };

  const handleSubmitContribution = async () => {
    if (!contributionData.amount || parseFloat(contributionData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!contributionData.transaction_id.trim()) {
      toast.error('Please enter transaction ID');
      return;
    }
    if (!contributionData.transaction_date) {
      toast.error('Please select transaction date');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/wishlists/${wishlist.id}/items/${selectedItem.id}/contribute`,
        {
          amount: parseFloat(contributionData.amount),
          transaction_id: contributionData.transaction_id.trim(),
          transaction_date: contributionData.transaction_date
        },
        { withCredentials: true }
      );
      toast.success('Contribution submitted! Waiting for host approval.');
      setShowContributeModal(false);
      fetchWishlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit contribution');
    } finally {
      setSubmitting(false);
    }
  };

  const copyUpi = () => {
    if (wishlist?.upi_id) {
      navigator.clipboard.writeText(wishlist.upi_id);
      setCopied(true);
      toast.success('UPI ID copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRequestAccess = async () => {
    try {
      await axios.post(`${API}/wishlists/request-access`, { share_code: shareCode }, { withCredentials: true });
      toast.success('Access request sent! Waiting for approval.');
      setAccessDenied(false);
      navigate('/wishlist/view-others');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request access');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 flex items-center justify-center">
        <Loader2 className="text-white animate-spin" size={32} />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 pb-20">
        <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4">
          <button onClick={() => navigate('/wishlist/view-others')} className="text-white flex items-center gap-2">
            <ArrowLeft size={22} /> Back
          </button>
        </div>
        <div className="p-4 text-center mt-20">
          <Gift className="mx-auto text-contribo-gold mb-4" size={64} />
          <h2 className="text-white text-xl font-bold mb-2">Access Required</h2>
          <p className="text-gray-400 text-sm mb-6">You need approval from the wishlist owner to view this.</p>
          <button
            onClick={handleRequestAccess}
            className="bg-contribo-gold text-contribo-navy px-6 py-3 rounded-lg font-bold"
          >Request Access</button>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4 shadow-xl">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/wishlist/view-others')} className="text-white">
            <ArrowLeft size={22} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-white truncate">{wishlist?.name}</h1>
            <p className="text-xs text-gray-200">by {owner?.name || 'Unknown'}</p>
          </div>
        </div>
      </div>

      {/* UPI Payment Banner */}
      {wishlist?.upi_id && (
        <div className="mx-4 mt-3 bg-green-500/20 border border-green-400/50 rounded-lg p-3">
          <p className="text-xs text-green-200 mb-1">Pay contributions to host&apos;s UPI:</p>
          <div className="flex items-center justify-between">
            <span className="text-white font-bold">{wishlist.upi_id}</span>
            <button onClick={copyUpi} className="text-green-300 flex items-center gap-1 text-xs">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Items */}
      <div className="p-4">
        {items.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="mx-auto text-gray-500 mb-3" size={48} />
            <p className="text-white font-semibold">No items in this wishlist</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(item => {
              const progress = item.price ? Math.min((item.funded_amount || 0) / item.price * 100, 100) : 0;
              const isFunded = progress >= 100;
              
              return (
                <div key={item.id} className={`bg-white rounded-xl shadow-lg overflow-hidden ${isFunded ? 'opacity-75' : ''}`}>
                  <div className="flex">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-28 h-28 object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-28 h-28 bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <ShoppingBag className="text-gray-400" size={28} />
                      </div>
                    )}
                    <div className="p-3 flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm line-clamp-2">{item.name}</h3>
                      {item.price && (
                        <p className="text-contribo-gold font-bold text-base mt-1">₹{item.price.toLocaleString()}</p>
                      )}
                      {item.description && (
                        <p className="text-gray-500 text-xs mt-1 line-clamp-1">{item.description}</p>
                      )}
                      
                      {/* Progress bar */}
                      {item.price && (
                        <div className="mt-2">
                          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full transition-all ${isFunded ? 'bg-green-500' : 'bg-contribo-gold'}`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-gray-500">
                              ₹{(item.funded_amount || 0).toLocaleString()} raised
                            </span>
                            {isFunded && (
                              <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                                <CheckCircle size={12} /> Funded!
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex border-t border-gray-100">
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-2.5 text-blue-600 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-blue-50"
                      >
                        <ExternalLink size={14} /> View Product
                      </a>
                    )}
                    {!isFunded && item.price && (
                      <button
                        onClick={() => handleContribute(item)}
                        className="flex-1 py-2.5 text-green-600 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-green-50 border-l border-gray-100"
                      >
                        <Heart size={14} /> Contribute
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Contribute Modal */}
      {showContributeModal && selectedItem && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <h2 className="text-lg font-bold text-gray-800 mb-2">Contribute</h2>
              <p className="text-gray-500 text-xs mb-4">Help fund: <span className="font-semibold">{selectedItem.name}</span></p>
              
              {/* Item preview */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4 flex items-center gap-3">
                {selectedItem.image_url ? (
                  <img src={selectedItem.image_url} alt={selectedItem.name} className="w-16 h-16 object-cover rounded-lg" />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                    <ShoppingBag className="text-gray-400" size={20} />
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-800">₹{selectedItem.price?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">₹{(selectedItem.price - (selectedItem.funded_amount || 0)).toLocaleString()} remaining</p>
                </div>
              </div>

              {/* Payment UPI */}
              {wishlist?.upi_id ? (
                <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-200">
                  <p className="text-xs text-green-700 font-semibold mb-1">Pay to Host&apos;s UPI:</p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-green-900">{wishlist.upi_id}</span>
                    <button onClick={copyUpi} className="text-green-600 flex items-center gap-1 text-xs">
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 rounded-lg p-3 mb-4 border border-yellow-200">
                  <p className="text-xs text-yellow-700">Host hasn&apos;t set up UPI yet. Contact them for payment details.</p>
                </div>
              )}

              {/* Amount */}
              <div className="mb-3">
                <label className="text-xs font-semibold text-gray-600 block mb-1">Contribution Amount (₹) *</label>
                <input
                  type="number"
                  value={contributionData.amount}
                  onChange={(e) => setContributionData({ ...contributionData, amount: e.target.value })}
                  placeholder={`Max: ${selectedItem.price - (selectedItem.funded_amount || 0)}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  min="1"
                />
              </div>

              {/* Transaction ID */}
              <div className="mb-3">
                <label className="text-xs font-semibold text-gray-600 block mb-1">Transaction ID *</label>
                <input
                  type="text"
                  value={contributionData.transaction_id}
                  onChange={(e) => setContributionData({ ...contributionData, transaction_id: e.target.value })}
                  placeholder="Enter UPI transaction ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Transaction Date */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-gray-600 block mb-1">Transaction Date *</label>
                <input
                  type="date"
                  value={contributionData.transaction_date}
                  onChange={(e) => setContributionData({ ...contributionData, transaction_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowContributeModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-600"
                >Cancel</button>
                <button
                  onClick={handleSubmitContribution}
                  disabled={submitting || !wishlist?.upi_id}
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-bold disabled:opacity-50"
                >{submitting ? 'Submitting...' : 'Submit'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
};

export default SharedWishlistViewPage;
